



















import {Component, Vue} from 'vue-property-decorator';
import { Inject } from 'vue-typedi';
import TotalOrderNumber from "@/components/TotalOrderNumber.vue";
import SearchBar from "@/components/SearchBar.vue";
import TabsContainer from "@/components/TabsContainer.vue";
import OrderPreparation from "@/components/orders/OrderPreparation.vue";
import { EventBus } from '@/helpers/EventBus';
import { OrdersList } from '@/model/OrdersList';
import { GetOrdersSearchCriteria, OrderService } from '@/services/OrderService';
import { Order } from '@/model/Order';
import { OrderedProductsList } from '@/model/OrderedProductsList';
import { OrderedProduct } from '@/model/OrderedProduct';
import { StoreService } from '@/services/StoreService';
import { OrderedProductService } from '@/services/OrderedProductService';
import {OrderState} from "@/model/OrderState";
import {Drive} from "@/model/Drive";
import {SerializationHelper} from "@/helpers/SerializationHelper";
import { FabricationEventsService } from '@/services/FabricationEventsService';
import { FabricationOrdersEvent } from '@/model/events/FabricationOrdersEvent';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { CalendarRange } from '@/model/calendar/CalendarRange';
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

@Component({
  components: {TotalOrderNumber, SearchBar, TabsContainer, OrderPreparation}
})
export default class GlobalBody extends Vue {

  private static readonly STORE_ID: string = "storeId";

  @Inject()
  orderService!: OrderService;
  @Inject()
  orderedProductService!: OrderedProductService;
  @Inject()
  storeService!: StoreService;
  @Inject()
  fabricationEventsService: FabricationEventsService;

  private refreshStoreInterval: any = null;

  private searchTerms: string = null;
  private fullOrdersList: OrdersList = null;
  private filteredOrdersList: OrdersList = null;
  private fullOrderedProductsList: OrderedProductsList = null;
  private filteredOrderedProductsList: OrderedProductsList = null;
  private selectedOrder: Order = null;
  private canHandleSupports: boolean = false;

  created() {
      this.refreshStore();

      this.refreshStoreInterval = setInterval(() => this.refreshStore(), 30000);

      if (this.isFabricationOrdersEventsEnabled) {
          this.fabricationEventsService.watchOrders();
          EventBus.$on(EventBus.ORDERS_LIST_SERVER_UPDATE, this.refreshEventData);
      }

      EventBus.$on(EventBus.UPDATE_SEARCH, this.updateSearchTerms);
      EventBus.$on(EventBus.CHANGE_ORDER_STATE, this.refreshAll);
      EventBus.$on(EventBus.CHANGE_ORDERED_PRODUCT_STATE, this.refreshAll);
      EventBus.$on(EventBus.UPDATE_SELECTED_ZONES, this.onUpdateSearch);
      EventBus.$on(EventBus.UPDATE_SELECTED_RANGE, this.onUpdateSearch);
      EventBus.$on(EventBus.UPDATE_FILTER_WITH_DELAY, this.onUpdateSearch);
      EventBus.$on(EventBus.UPDATE_FILTER_WITH_MISSING, this.onUpdateSearch);
      EventBus.$on(EventBus.UPDATE_FILTER_WITH_CATERING_SERVICE, this.onUpdateSearch);
      EventBus.$on(EventBus.REFRESH_STORE, this.refreshAll);
      EventBus.$on(EventBus.UPDATE_PRODUCT_OUT_OF_STOCK_STATUS, this.refreshAll);
      EventBus.$on(EventBus.PREPARE_ORDER, this.setSelectedOrder);
      EventBus.$on(EventBus.CANCEL_ORDER_PREPARATION, this.cancelOrderPreparation);
  }

  beforeDestroy() {
      clearInterval(this.refreshStoreInterval);

      if (this.isFabricationOrdersEventsEnabled) {
          this.fabricationEventsService.unwatchOrders();
          EventBus.$off(EventBus.ORDERS_LIST_SERVER_UPDATE, this.refreshEventData);
      }

      EventBus.$off(EventBus.UPDATE_SEARCH, this.updateSearchTerms);
      EventBus.$off(EventBus.CHANGE_ORDER_STATE, this.refreshAll);
      EventBus.$off(EventBus.CHANGE_ORDERED_PRODUCT_STATE, this.refreshAll);
      EventBus.$off(EventBus.UPDATE_SELECTED_ZONES, this.onUpdateSearch);
      EventBus.$off(EventBus.UPDATE_SELECTED_RANGE, this.onUpdateSearch);
      EventBus.$off(EventBus.UPDATE_FILTER_WITH_DELAY, this.onUpdateSearch);
      EventBus.$off(EventBus.UPDATE_FILTER_WITH_MISSING, this.onUpdateSearch);
      EventBus.$off(EventBus.UPDATE_FILTER_WITH_CATERING_SERVICE, this.onUpdateSearch);
      EventBus.$off(EventBus.REFRESH_STORE, this.refreshAll);
      EventBus.$off(EventBus.UPDATE_PRODUCT_OUT_OF_STOCK_STATUS, this.refreshAll);
      EventBus.$off(EventBus.PREPARE_ORDER, this.setSelectedOrder);
      EventBus.$off(EventBus.CANCEL_ORDER_PREPARATION, this.cancelOrderPreparation);
  }

  private refreshStore() {
      this.storeService.refreshStore();
      this.computeCanHandleSupports();
  }

  private refreshEventData(event: FabricationOrdersEvent): void {
      this.refreshOrders(this.convertEventToOrdersList(event));
      this.refreshOrderedProducts(this.convertEventToOrderedProductsList(event));
  }

  private convertEventToOrdersList(event: FabricationOrdersEvent): OrdersList {
      const ordersList: OrdersList = new OrdersList();
      ordersList.summary = event?.summary;
      ordersList.ordersToProcess = event?.orders?.ordersToProcess;
      ordersList.ordersProcessed = event?.orders?.ordersProcessed;
      ordersList.ordersArchived = event?.orders?.ordersArchived;

      return ordersList;
  }

  private convertEventToOrderedProductsList(event: FabricationOrdersEvent): OrderedProductsList {
      const productsList: OrderedProductsList = new OrderedProductsList();
      productsList.summary = event?.summary;
      productsList.productsToProcess = event?.products?.productsToProcess;
      productsList.productsProcessed = event?.products?.productsProcessed;

      return productsList;
  }

  private refreshOrders(ordersList?: OrdersList) {
      const callback = (data: OrdersList) => {
          this.fullOrdersList = data;
          this.filteredOrdersList = this.filterOrders(data, this.searchTerms);
          EventBus.$emit(EventBus.REFRESH_ORDERS_LIST, this.filteredOrdersList);
      };

      if (this.isFabricationOrdersEventsEnabled && ordersList) {
        callback(ordersList);
      } else {
        const criteria = {
          store: sessionStorage.getItem(GlobalBody.STORE_ID),
          zones: this.storeService.selectedAndEnabledZones
        } as GetOrdersSearchCriteria;

        this.orderService.getOrders(criteria).then(data => callback(data));
      }
  }

  private refreshOrderedProducts(productsList?: OrderedProductsList) {
      const callback = (data: OrderedProductsList) => {
          this.fullOrderedProductsList = data;
          this.filteredOrderedProductsList = this.filterProducts(data, this.searchTerms);
          EventBus.$emit(EventBus.REFRESH_ORDERED_PRODUCTS_LIST, this.filteredOrderedProductsList);
      };

      if (this.isFabricationOrdersEventsEnabled && productsList) {
          callback(productsList);
      } else {
          this.orderedProductService.getOrderedProducts().then(data => callback(data));
      }
  }

  private filterOrders(fullOrdersList: OrdersList, searchTerms: string): OrdersList {
      const searchInput: string = searchTerms == null ? null : searchTerms.toLowerCase();

      const filteredOrdersList = new OrdersList();
      filteredOrdersList.summary = fullOrdersList.summary;
      filteredOrdersList.ordersToProcess = this.filterOrdersList(fullOrdersList.ordersToProcess, searchInput);
      filteredOrdersList.ordersProcessed = this.filterProcessedOrdersList(fullOrdersList.ordersProcessed, searchInput);
      filteredOrdersList.ordersArchived = this.filterProcessedOrdersList(fullOrdersList.ordersArchived, searchInput);

      return filteredOrdersList;
  }

  private filterOrdersList(orders: Order[], searchTerms: string): Order[] {
    const selectedZonesArray: string[] = this.storeService.selectedAndEnabledZones;
    const hasSelectedZones: boolean = selectedZonesArray != null && selectedZonesArray.length !== 0;
    const selectedRange: CalendarRange = this.storeService.selectedRange;
    const hasSelectedRange: boolean = selectedRange != null && selectedRange.startDate != null && selectedRange.endDate != null;

    if (this.storeService.isOrderWithDelay) {
      return orders.filter(order => this.isMatchingWithSearchTerms(searchTerms, order)
        && (!hasSelectedZones || this.isMatchingWithSelectedZones(selectedZonesArray, order))
        && this.hasProductsWithDelay(order)
        && (!hasSelectedRange || this.isMatchingWithSelectedRange(selectedRange, order))
        && (!this.storeService.isOrderWithCateringService || order?.isCateringService)
      );
    }
    return orders.filter(order => this.isMatchingWithSearchTerms(searchTerms, order)
      && (!hasSelectedZones || this.isMatchingWithSelectedZones(selectedZonesArray, order))
      && (!hasSelectedRange || this.isMatchingWithSelectedRange(selectedRange, order))
      && (!this.storeService.isOrderWithCateringService || order?.isCateringService)
    );
  }

  private isMatchingWithSelectedRange(range: CalendarRange, order: Order): boolean {
    const pickupDate = dayjs(order.pickupDate);
    return pickupDate.isSameOrAfter(dayjs(range.startDate), 'day')
        && pickupDate.isSameOrBefore(dayjs(range.endDate), 'day');
  }

  private filterProcessedOrdersList(orders: Order[], searchTerms: string): Order[] {
    const selectedZonesArray: string[] = this.storeService.selectedAndEnabledZones;
    const hasSelectedZones: boolean = selectedZonesArray != null && selectedZonesArray.length !== 0;
    const selectedRange: CalendarRange = this.storeService.selectedRange;
    const hasSelectedRange: boolean = selectedRange != null && selectedRange.startDate != null && selectedRange.endDate != null;

    if (this.storeService.isOrderWithMissing) {
      return orders.filter(order => this.isMatchingWithSearchTerms(searchTerms, order)
        && (!hasSelectedZones || this.isMatchingWithSelectedZones(selectedZonesArray, order)) && this.hasProductsWithMissing(order)
        && (!hasSelectedRange || this.isMatchingWithSelectedRange(selectedRange, order))
        && (!this.storeService.isOrderWithCateringService || order?.isCateringService)
      );
    }
    return this.filterOrdersList(orders, searchTerms);
  }

  private hasProductsWithDelay(order: Order): boolean {
    return order?.products?.some(product => product?.withDelay === this.storeService?.isOrderWithDelay) ?? false;
  }

  private hasProductsWithMissing(order: Order): boolean {
      return order?.products?.some(product => product?.processedQuantity && (product?.processedQuantity.value < product?.quantity.value)) ?? false;
  }

  private isMatchingWithSelectedZones(selectedZonesArray: string[] | null, order: Order): boolean {
    return selectedZonesArray?.includes(order?.zone) ?? false;
  }

  private isMatchingWithSearchTerms(searchTerms: string | null, order: Order): boolean {
    return searchTerms?.trim() === '' || searchTerms === null || order?.orderNumber?.includes(searchTerms);
  }

  private filterProducts(fullProductsList: OrderedProductsList, searchTerms: string): OrderedProductsList {
      const searchInput: string = searchTerms == null ? null : searchTerms.toLowerCase();

      const filteredProductsList = new OrderedProductsList();
      filteredProductsList.summary = fullProductsList.summary;
      filteredProductsList.productsToProcess = this.filterProductsList(fullProductsList.productsToProcess, searchInput);
      filteredProductsList.productsProcessed = this.filterProductsList(fullProductsList.productsProcessed, searchInput);

      return filteredProductsList;
  }

  private filterProductsList(orderedProducts: OrderedProduct[], searchTerms: string): OrderedProduct[] {
      if (!searchTerms) {
          return orderedProducts;
      }

      return orderedProducts.filter(orderedProduct => {
          return orderedProduct.product.cug.includes(searchTerms) 
                || (orderedProduct.product.label != null && orderedProduct.product.label.toLowerCase().includes(searchTerms))
                || orderedProduct.relatedOrders.find(order => order.orderNumber.includes(searchTerms));
      });
  }

  private updateSearchTerms(searchTerms: string): void {
      this.searchTerms = searchTerms;
      this.onUpdateSearch();
  }

  private onUpdateSearch(): void {
      if (this.fullOrdersList == null) {
          return;
      }

      this.filteredOrdersList = this.filterOrders(this.fullOrdersList, this.searchTerms);
      EventBus.$emit(EventBus.REFRESH_ORDERS_LIST, this.filteredOrdersList);

      this.filteredOrderedProductsList = this.filterProducts(this.fullOrderedProductsList, this.searchTerms);
      EventBus.$emit(EventBus.REFRESH_ORDERED_PRODUCTS_LIST, this.filteredOrderedProductsList);
  }

  private refreshAll(): void {
      if (!this.isFabricationOrdersEventsEnabled) {
        this.refreshOrders();
        this.refreshOrderedProducts();
      }
  }

  private setSelectedOrder(order: Order): void {
    this.selectedOrder = order;
  }

  private cancelOrderPreparation(): void {
    this.changeOrderState(OrderState.TO_PROCESS);
    this.selectedOrder = null;
  }

  private changeOrderState(newState: OrderState): void {
    this.orderService.changeOrderState(this.selectedOrder.orderNumber, this.selectedOrder.zone, newState, this.selectedOrder.products)
      .then((res: Order[]) => {
        if (res == null) {
          return;
        }

        for (let order of res) {
          if (newState === OrderState.PROCESSING) {
            EventBus.$emit(EventBus.PREPARE_ORDER, order);
          }
          EventBus.$emit(EventBus.CHANGE_ORDER_STATE, order);
        }
      });
  }

  private computeCanHandleSupports(): void {
    const storeDrives = sessionStorage.getItem("storeDrives");
    if (storeDrives == null) {
      this.canHandleSupports = false;
      return;
    }

    const drives: Drive[] =
      SerializationHelper.toArrayInstanceFromJsonStringArray(
        () => new Drive(),
        storeDrives
      );

    this.canHandleSupports = drives.some(d => d.wms === 'MAWM');
  }

  get isFabricationOrdersEventsEnabled(): boolean {
    const config = (window as any)?.config;

    return config?.events?.enabled && config?.events?.fabrication?.enabled;
  }

  get effectiveCanHandleSupports(): boolean {
    if (this.selectedOrder?.isCateringService) {
      return false;
    }
    return this.canHandleSupports;
  }
}
